import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { Keyboard } from '@capacitor/keyboard';
import { isNativePlatform } from '@/lib/platform';
import {
  blurActiveEditableElement,
  scrollFocusedElementIntoView,
} from '@/utils/keyboardInset';

export interface UseKeyboardAwareContainerOptions {
  /** When false, listeners are not attached. */
  enabled?: boolean;
}

/**
 * Attach focus/resize handlers so editable fields inside a scroll container
 * stay visible above the software keyboard.
 */
export function useKeyboardAwareContainer(
  options: UseKeyboardAwareContainerOptions = {},
): {
  containerRef: RefObject<HTMLDivElement | null>;
  setContainerRef: (el: HTMLDivElement | null) => void;
  scrollFocusedIntoView: () => void;
  blurActiveInput: () => void;
} {
  const { enabled = true } = options;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(null);

  const setContainerRef = useCallback((el: HTMLDivElement | null) => {
    containerRef.current = el;
    setContainerNode(el);
  }, []);

  const scrollFocusedIntoView = useCallback(() => {
    scrollFocusedElementIntoView(containerRef.current);
  }, []);

  const blurActiveInput = useCallback(() => {
    blurActiveEditableElement();
  }, []);

  useEffect(() => {
    if (!enabled || !containerNode) return;

    const native = isNativePlatform();

    const handleFocusIn = (): void => {
      // Delay on native so keyboardWillShow/layout settle before any scroll nudge.
      const run = (): void => {
        scrollFocusedElementIntoView(containerNode);
      };
      if (native) {
        window.setTimeout(run, 120);
      } else {
        requestAnimationFrame(run);
      }
    };

    containerNode.addEventListener('focusin', handleFocusIn);

    // visualViewport resize during keyboard open causes scroll loops on iOS native.
    const vv = native ? null : window.visualViewport;
    const handleViewportChange = (): void => {
      scrollFocusedElementIntoView(containerNode);
    };
    vv?.addEventListener('resize', handleViewportChange);
    vv?.addEventListener('scroll', handleViewportChange);

    // The 120ms focusin delay above is a guess at when the keyboard has settled.
    // keyboardDidShow fires once the show animation actually completes (with the
    // final height, e.g. once the QuickType/autofill bar is accounted for), so
    // re-run the nudge then too — this is what catches the field once typing starts
    // if the earlier guess ran too early.
    const didShowListener = native
      ? Keyboard.addListener('keyboardDidShow', () => {
          scrollFocusedElementIntoView(containerNode);
        })
      : null;

    return () => {
      containerNode.removeEventListener('focusin', handleFocusIn);
      vv?.removeEventListener('resize', handleViewportChange);
      vv?.removeEventListener('scroll', handleViewportChange);
      didShowListener?.then((h) => h.remove()).catch(() => {});
    };
  }, [enabled, containerNode]);

  return { containerRef, setContainerRef, scrollFocusedIntoView, blurActiveInput };
}
